// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { ApiException } from '@digitalasset/validator-scan-proxy-api-ts-client';
import { useAccounts } from '@hooks/dapp/useAccounts';
import { useConnect } from '@hooks/dapp/useConnect';
import { useConfig } from '@hooks/other/useConfig';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeToken, generateToken } from '../utils/auth';
import { useIdpInfo } from './IdpContext';
import { LoggedInUser, UserContext } from './UserContext';

const isApiException = (error: unknown): error is ApiException<unknown> => {
  return error !== null && typeof error === 'object' && 'code' in error;
};
export const UserProvider = ({ children }: PropsWithChildren) => {
  // Global Hooks
  const nav = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { enableDappLogin } = useConfig().app;
  const { idpInfo, unsetIdpInfo, setIdpError } = useIdpInfo();

  // Auth Hooks
  const oidcAuth = useAuth();
  const { disconnect, status } = useConnect();
  // Local State
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();

  // Derived State
  const isConnected = status?.isConnected;
  const accounts = useAccounts(isConnected);
  const dappUserId = status?.session?.userId;
  const tokenFromDapp = status?.session?.accessToken;
  const hasAccounts = accounts ? accounts.length > 0 : false;
  const primaryAccount = useMemo(() => accounts?.find((a) => a.primary), [accounts]);
  // =========================================
  // EFFECT: Dapp Wallet Sync & Alerts
  // =========================================
  useEffect(() => {
    // 1. Guard Clauses: Only run if Dapp login is enabled
    if (!enableDappLogin) return;

    // 2. Alert if connected but no primary party
    if (isConnected && accounts !== undefined && !primaryAccount) {
      enqueueSnackbar('No primary party set. You may need to synchronize wallets.', {
        variant: 'info',
      });
    }

    // 3. Sync User State
    if (isConnected && dappUserId && tokenFromDapp && primaryAccount) {
      setLoggedInUser({
        userId: dappUserId,
        userAccessToken: tokenFromDapp,
        primaryParty: primaryAccount.partyId,
        isConnectedViaDapp: true,
      });
    } else {
      // If we lost session or primary account, clear user
      setLoggedInUser(undefined);
    }
  }, [
    isConnected,
    enableDappLogin,
    accounts,
    dappUserId,
    tokenFromDapp,
    primaryAccount,
    enqueueSnackbar,
    hasAccounts,
  ]);

  // =========================================
  // EFFECT: OIDC (Standard Login) Sync
  // =========================================
  useEffect(() => {
    // Only run for RS-256 (Standard OIDC)
    if (idpInfo?.algorithm !== 'rs-256') return;

    // Ensure we are authenticated before processing
    if (!oidcAuth?.isAuthenticated || !oidcAuth?.user) return;

    const userId = decodeToken(oidcAuth.user.access_token).sub!;

    // Prevent unnecessary state updates if user is already set
    setLoggedInUser((prev) => {
      if (prev?.userId === userId && prev?.userAccessToken === oidcAuth.user?.access_token) {
        return prev;
      }
      return {
        userId,
        userAccessToken: oidcAuth.user!.access_token,
      };
    });
  }, [idpInfo, oidcAuth]);

  // =========================================
  // EFFECT: Navigation Redirects
  // =========================================
  useEffect(() => {
    // If we have a user and are still on the login page, go home
    if (loggedInUser && location.pathname.includes('/login')) {
      nav('/');
    }
  }, [loggedInUser, location, nav]);

  const login = useCallback(
    async (userId?: string) => {
      // Case 1: Unsafe Local Development (HS-256)
      if (idpInfo?.algorithm === 'hs-256-unsafe') {
        if (!userId) throw new Error("User ID required for 'HS256' login");

        const userAccessToken = await generateToken(
          userId,
          idpInfo.secret,
          idpInfo.audience,
          idpInfo.scope,
        );
        setLoggedInUser({ userId, userAccessToken });
        return;
      }

      // Case 2: Standard OIDC (RS-256)
      const isReadyToLogin =
        idpInfo?.algorithm === 'rs-256' &&
        !hasAuthParams() &&
        !oidcAuth.isAuthenticated &&
        !oidcAuth.activeNavigator &&
        !oidcAuth.isLoading;

      if (isReadyToLogin) {
        console.debug('User is unauthenticated. Signing in.');
        try {
          await oidcAuth.signinRedirect();
        } catch (err) {
          // Catching specific redirect errors here
          console.error('Sign in redirect failed', err);
        }

        // Note: oidcAuth.error might not be populated immediately after await due to closures
        if (oidcAuth.error) {
          unsetIdpInfo();
          setIdpError(
            'There is an error with the identity provider configuration, please try again or contact your administrator.',
          );
        }
      } else {
        console.debug('Authentication process is still executing or not ready.');
      }
    },
    [idpInfo, setIdpError, unsetIdpInfo, oidcAuth],
  );

  const logout = useCallback(async () => {
    if (enableDappLogin) {
      await disconnect();
    }
    setLoggedInUser(undefined);

    if (idpInfo?.algorithm === 'rs-256' && oidcAuth) {
      unsetIdpInfo();
      await oidcAuth.signoutRedirect({
        id_token_hint: oidcAuth.user?.id_token,
        post_logout_redirect_uri: window.location.origin,
      });
    }
  }, [idpInfo, oidcAuth, unsetIdpInfo, disconnect, enableDappLogin]);

  const signoutOnUnauthorized = useCallback(
    (err: unknown) => {
      if (isApiException(err) && err.code === 401) {
        logout().catch(() => console.debug('There was an issue redirecting the user to logout.'));
      }
    },
    [logout],
  );

  const isSelfSignedToken = useCallback(() => idpInfo?.algorithm === 'hs-256-unsafe', [idpInfo]);

  const userState = useMemo(
    () => ({
      loggedInUser,
      login,
      logout,
      isSelfSignedToken,
      signoutOnUnauthorized,
    }),
    [loggedInUser, login, logout, isSelfSignedToken, signoutOnUnauthorized],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};
